/**
 * Reads the two readable halves of a cookie session.
 *
 * The session JWT itself lives in `gemsby_session`, which is HttpOnly — this
 * module cannot see it and neither can anything else running on the page, which
 * is the entire point of moving it out of localStorage. What is left for script
 * to read is deliberately non-secret:
 *
 *  - gemsby_csrf  the double-submit token. Because the browser now attaches the
 *                 session cookie to any request it is caused to make, mutating
 *                 calls have to prove they came from our own page; echoing this
 *                 value in X-XSRF-Token is that proof, since another origin can
 *                 cause the cookie to be sent but cannot read it.
 *
 *  - gemsby_user  id/username/type/retailerType. The app decides "is someone
 *                 signed in?" during first paint and used to answer it from
 *                 localStorage; without this every reload would paint a
 *                 signed-out shell and correct itself a round-trip later.
 *
 * Names are overridable because the server side takes them from AUTH_*_NAME env
 * vars — a deployment that renames them has to rename them on both sides.
 */

// Scoped per app. All three frontends sit on one parent domain
// (AUTH_COOKIE_DOMAIN), so each receives the others' cookies — reading the
// unsuffixed name meant every app saw whichever session was created last. There
// is deliberately no fallback to the unsuffixed name: a leftover shared cookie
// would put the cross-app bleed straight back. A visitor still holding one simply
// appears signed out and signs in once, and that login retires it (see
// SessionCookie::forgetLegacyShared server-side).
const APP = 'admin'
const CSRF_COOKIE = `${
  import.meta.env.VITE_AUTH_CSRF_COOKIE_NAME || 'gemsby_csrf'
}_${APP}`
const HINT_COOKIE = `${
  import.meta.env.VITE_AUTH_HINT_COOKIE_NAME || 'gemsby_user'
}_${APP}`

function readCookie(name) {
  // document.cookie is one flat string, and a value may legitimately contain
  // '=' (the hint cookie is base64-ish JSON), so split on the first one only.
  const prefix = `${name}=`
  const found = document.cookie
    .split('; ')
    .find(part => part.startsWith(prefix))

  if (!found) {
    return null
  }

  const value = found.slice(prefix.length)

  return value === '' ? null : value
}

export function csrfToken() {
  return readCookie(CSRF_COOKIE)
}

/**
 * The signed-in user, or null. Shaped like the `currentUser` object the app
 * used to keep in localStorage so callers do not have to change.
 */
export function identityHint() {
  const raw = readCookie(HINT_COOKIE)

  if (!raw) {
    return null
  }

  try {
    // Laravel URL-encodes cookie values on the way out; the JSON inside is what
    // SessionCookie::hint() built.
    const parsed = JSON.parse(decodeURIComponent(raw))

    // An id and a username are what every caller downstream assumes it has.
    // A hint missing either is not usable as an identity, so treat it as absent
    // rather than handing back a half-populated user.
    if (!parsed || !parsed.id || !parsed.username) {
      return null
    }

    return parsed
  } catch (e) {
    return null
  }
}

export default { csrfToken, identityHint }
